import { useState } from 'react'
import { api, type Product } from '../lib/api.ts'
import { Button } from './Button.tsx'
import { useConfirm } from './Confirm.tsx'
import { useToast } from './Toast.tsx'

/**
 * Whether this pharmacy treats the product as an excipient, over the ERP's type.
 *
 * The ERP types each keyed line, and a filler it keys as a plain component
 * (`CELULOSIX LIB MODIFICADA`, `TRANSCUTOL`) is compared with the receita as an
 * ingredient. The conferente overrides that from ✗ on a conferência; this is
 * where the override is seen, and taken back or given, at any time (ADR 0014).
 */
export function ExcipientOverride({
  product,
  onChanged,
}: {
  product: Product
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const confirm = useConfirm()
  const toast = useToast()
  const override = product.excipientOverride

  async function toggle() {
    const sure = await confirm(
      override === null
        ? {
            title: `Tratar ${product.name} como excipiente?`,
            message:
              'Deixa de ser comparado com a receita em todas as próximas conferências desta farmácia, mesmo quando o ERP o registra como componente da fórmula. Dá para desfazer aqui a qualquer momento.',
            confirmLabel: 'Tratar como excipiente',
            tone: 'danger',
          }
        : {
            title: `Voltar a seguir o ERP para ${product.name}?`,
            message:
              'Onde o ERP o registrar como componente, volta a ser comparado com a receita nas próximas conferências.',
            confirmLabel: 'Seguir o ERP',
          },
    )
    if (!sure) return
    setBusy(true)
    try {
      if (override === null) await api.markExcipientProduct(product.erpCode)
      else await api.unmarkExcipient(product.erpCode)
      toast.show(
        override === null
          ? `${product.name} tratado como excipiente`
          : `${product.name} volta a seguir o tipo do ERP`,
        { tone: 'success' },
      )
      onChanged()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao alterar', { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col items-start gap-2">
      <p className="text-tP text-text-strong">
        {override === null
          ? 'Segue o tipo que o ERP informa em cada orçamento'
          : 'Excipiente — definido nesta farmácia, por cima do tipo do ERP'}
      </p>
      {override !== null && (
        <p className="text-tSmall text-text-weak">{overrideOrigin(override)}</p>
      )}
      <Button
        size="small"
        variant="secondary"
        tone={override === null ? 'neutral' : 'danger'}
        disabled={busy}
        onClick={() => void toggle()}
      >
        {override === null ? 'Tratar como excipiente' : 'Remover marcação'}
      </Button>
    </div>
  )
}

/** Who set the override, when, and from which conferência. */
export function overrideOrigin(override: NonNullable<Product['excipientOverride']>): string {
  const at = new Date(override.markedAt)
  const when = Number.isNaN(at.getTime())
    ? ''
    : ` em ${at.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`
  const from = override.markedFrom
  const where =
    from === null || from.quotationNumber === null
      ? ''
      : ` · na conferência ${from.branchCode ?? '?'}/${from.quotationNumber}`
  return `Marcado por ${override.markedBy}${when}${where}`
}
